import type { ReviewPayload } from './reviewLens';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Talking to the review-portal edge function.
 *
 * Every call carries the token, and the token is the entire credential — the
 * caller is not signed in and never will be. That is why nothing here holds
 * state between calls and why each action re-sends it: there is no session to
 * put it in, and inventing one in localStorage would be a session with no
 * expiry that the server does not know about.
 *
 * functions.invoke uses the publishable anon key, which is what the function
 * expects. It is deployed with verify_jwt = false (supabase/config.toml), so
 * no user JWT is required or checked — the token is validated inside, against
 * review_token_order, which is the single place expiry and revocation are
 * decided together.
 */

export type ReviewErrorCode =
  | 'not_found'
  | 'already_decided'
  | 'already_signed'
  | 'not_all_components_approved'
  | 'note_required'
  | 'signoff_incomplete'
  | 'signoff_failed'
  | 'bad_request'
  | 'unreachable';

export class ReviewError extends Error {
  code: ReviewErrorCode;
  constructor(code: ReviewErrorCode) {
    super(code);
    this.code = code;
  }
}

/**
 * What each failure is called in front of a client.
 *
 * Written for somebody with no account, no context and nobody to ask. "404"
 * and "already_decided" are notes to ourselves; these are the versions that
 * tell a stranger what happened and what to do next.
 */
export const REVIEW_ERROR_MESSAGES: Record<ReviewErrorCode, string> = {
  not_found:
    'This review link is not valid any more. It may have expired, or been replaced by a newer one. Please ask your 4ocean contact for a fresh link.',
  already_decided:
    'That component has already been decided. Refresh the page to see where the order stands.',
  already_signed: 'This order has already been signed off. Refresh to see the certificate.',
  not_all_components_approved:
    'Every component needs to be approved before the order can be signed off.',
  note_required: 'Tell us what needs to change before sending it back.',
  signoff_incomplete: 'Please fill in your name, email and title, and tick the confirmation.',
  signoff_failed:
    'The sign-off could not be completed. Please try again, or reply to the email thread that brought you here.',
  bad_request: 'Something about that request was not understood. Please refresh and try again.',
  /**
   * The last resort, and it must not claim to know which of the two happened.
   *
   * This message used to read "We could not reach the server. Check your
   * connection and try again." — under a heading saying "This link is no
   * longer active". A client with an expired link was told their internet was
   * broken, would retry, fail, and email asking why the site was down. Two
   * sentences on one screen, contradicting each other, and only one of them
   * had been checked.
   */
  unreachable:
    'We could not load this page. Your link may have expired, or the server could not be reached. Please try again — and if it keeps happening, ask your 4ocean contact for a fresh link.',
};

/**
 * The error code the function sent, dug out of a non-2xx response.
 *
 * supabase-js turns any non-2xx into a FunctionsHttpError, leaves `data` null,
 * and puts the actual response on `error.context` — so the code the function
 * carefully sent (`not_found`, `already_signed`) is present and simply never
 * read. That is why a dead link produced the generic network message: not
 * because the information was missing, but because nobody opened the envelope.
 *
 * Everything here is defensive on purpose. `context` may be absent on other
 * error shapes, the body may not be JSON, and a Response body can only be read
 * once — none of which should turn a bad link into an exception on a public
 * page.
 */
async function codeFromError(error: unknown): Promise<ReviewErrorCode | null> {
  const context: unknown = (error as { context?: unknown }).context;
  if (context === null || typeof context !== 'object') return null;
  const asJson: unknown = (context as { json?: unknown }).json;
  if (typeof asJson !== 'function') return null;
  try {
    const body: unknown = await (asJson as () => Promise<unknown>).call(context);
    const code: unknown = (body as { error?: unknown } | null)?.error;
    if (typeof code !== 'string') return null;
    return code in REVIEW_ERROR_MESSAGES ? (code as ReviewErrorCode) : null;
  } catch {
    // A body that is not JSON, or one already consumed. Neither is worth
    // failing over: the caller falls back to the honest generic above.
    return null;
  }
}

export function reviewErrorMessage(cause: unknown): string {
  if (cause instanceof ReviewError) {
    return REVIEW_ERROR_MESSAGES[cause.code];
  }
  return REVIEW_ERROR_MESSAGES.unreachable;
}

async function call(
  client: SupabaseClient,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const result = (await client.functions.invoke('review-portal', { body })) as {
    data: Record<string, unknown> | null;
    error: unknown;
  };

  // supabase-js reports any non-2xx as an error and does not surface the body,
  // so the code the function sent is read off the payload when it is there and
  // falls back to the safe generic when it is not.
  const code = typeof result.data?.error === 'string' ? result.data.error : null;
  if (code !== null) {
    throw new ReviewError(code as ReviewErrorCode);
  }
  if (result.error !== null && result.error !== undefined) {
    // Read the envelope before giving up on it.
    throw new ReviewError((await codeFromError(result.error)) ?? 'unreachable');
  }
  return result.data ?? {};
}

export async function loadReview(client: SupabaseClient, token: string): Promise<ReviewPayload> {
  const data = await call(client, { token, action: 'load' });
  const components = Array.isArray(data.components)
    ? data.components.filter((value): value is string => typeof value === 'string')
    : undefined;
  return {
    order: (data.order ?? {
      order_no: '',
      client: '',
      shipping_handling_usd: 0,
    }) as ReviewPayload['order'],
    items: (Array.isArray(data.items) ? data.items : []) as ReviewPayload['items'],
    assets: (Array.isArray(data.assets) ? data.assets : []) as ReviewPayload['assets'],
    certificate: (data.certificate ?? null) as ReviewPayload['certificate'],
    ...(components === undefined ? {} : { components }),
  };
}

/** Decide one component. The others are untouched — see reviewLens. */
export async function decideComponent(
  client: SupabaseClient,
  input: {
    token: string;
    assetId: string;
    decision: 'approved' | 'revision_requested';
    note: string;
  },
): Promise<ReviewPayload> {
  const data = await call(client, {
    token: input.token,
    action: 'decide',
    assetId: input.assetId,
    decision: input.decision,
    note: input.note,
  });
  const components = Array.isArray(data.components)
    ? data.components.filter((value): value is string => typeof value === 'string')
    : undefined;
  return {
    order: (data.order ?? {
      order_no: '',
      client: '',
      shipping_handling_usd: 0,
    }) as ReviewPayload['order'],
    items: (Array.isArray(data.items) ? data.items : []) as ReviewPayload['items'],
    assets: (Array.isArray(data.assets) ? data.assets : []) as ReviewPayload['assets'],
    certificate: (data.certificate ?? null) as ReviewPayload['certificate'],
    ...(components === undefined ? {} : { components }),
  };
}

export interface SignoffResult {
  certificate_no: string;
  client_name: string;
  signed_at: string;
}

export async function signOff(
  client: SupabaseClient,
  input: {
    token: string;
    name: string;
    email: string;
    title: string;
    confirmed: boolean;
  },
): Promise<SignoffResult> {
  const data = await call(client, {
    token: input.token,
    action: 'signoff',
    name: input.name,
    email: input.email,
    title: input.title,
    confirmed: input.confirmed,
  });
  return data.certificate as SignoffResult;
}
